import classNames from "classnames";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { Container, Row } from "../components";
import { buildPathWithParams, useMediaQuery } from "../hooks";
import { useAnalytics } from "../lib/analytics/analytics";
import { Locale, useI18n } from "../lib/i18n/i18n_provider";
import { useOfferingsMenuQuery } from "./api";
import { Hidden } from "./hidden";
import { LocaleOption } from "./locale_data";
import { LocalePicker } from "./locale_picker";
import { OfferingMenuOneLine, OfferingMenuPicker } from "./offering_header";
import { Spacer } from "./spacer";
import { TalkWithUsPrompt } from "./template_offering_checkout_common";

interface OfferingFooterMobileProps {
  showLanguageSelector: boolean;
  showTalkWithUsPrompt?: boolean;
  hubspotForm?: React.ReactNode;
  localeOptions?: LocaleOption[];
  partnerID: string;
  offeringID: string;
  onLocaleSwitch?: () => void;
}

/*
  Mobile-view component that has equivalent contents as its counterpart desktop-view OfferingHeader
*/
export function OfferingFooterMobile(props: OfferingFooterMobileProps) {
  const {
    hubspotForm,
    showLanguageSelector,
    localeOptions,
    showTalkWithUsPrompt = true,
    partnerID,
    offeringID,
    onLocaleSwitch,
  } = props;
  const { locale, onChangeLocale } = useI18n();
  const offeringMenu = useOfferingsMenuQuery(partnerID);
  const mq = useMediaQuery();
  const divRef = useRef<HTMLDivElement>(null);
  const OFFERING_FOOTER_MOBILE_HORIZONTAL_PADDING = 40;
  const [overflow, setOverflow] = useState<boolean | null>(null);
  const history = useHistory();
  const analytics = useAnalytics();

  function doesContentExceedPaddedScreenWidth(
    contentWidth: number,
    screenWidth: number
  ) {
    return (
      contentWidth > screenWidth - OFFERING_FOOTER_MOBILE_HORIZONTAL_PADDING
    );
  }

  useEffect(() => {
    if (divRef.current && overflow === null) {
      if (
        doesContentExceedPaddedScreenWidth(
          divRef.current.clientWidth,
          mq.dimensions.width
        )
      ) {
        setOverflow(true);
      } else {
        setOverflow(false);
      }
    }
  }, [divRef, mq, overflow]);

  const onOfferingMenuClick = useCallback(
    (menu: string) => {
      analytics.event("Menu Clicked", {
        label: menu,
        category: "Behavior",
        Source: "WL",
      });

      // We don't want to keep previous filter when going to a new offering listing page
      const pathWithParams = buildPathWithParams(`/offerings/${menu}`, {
        reset: true,
      });
      history.push(pathWithParams);
    },
    [analytics, history]
  );

  const onLocalePickerSelect = useCallback(
    (locale: Locale) => {
      onChangeLocale(locale);
      if (onLocaleSwitch) onLocaleSwitch();
    },
    [onChangeLocale, onLocaleSwitch]
  );

  return (
    <Container>
      <Hidden md>
        {offeringMenu?.showOfferingMenu &&
          (overflow ? (
            <Container center>
              <Container>
                <OfferingMenuPicker
                  offeringMenu={offeringMenu}
                  offeringID={offeringID}
                  onOfferingMenuClick={onOfferingMenuClick}
                />
                <Spacer size={24} />
              </Container>
            </Container>
          ) : (
            <Fragment>
              <div
                className={classNames(
                  "offering-menu-size-detector",
                  overflow === false && "offering-menu-one-line"
                )}
                ref={divRef}
              >
                <Row>
                  <OfferingMenuOneLine
                    offeringMenu={offeringMenu}
                    onOfferingMenuClick={onOfferingMenuClick}
                  />
                </Row>
              </div>
              <Spacer size={24} />
            </Fragment>
          ))}
        {showLanguageSelector && localeOptions && (
          <Container center>
            <Container width={130}>
              <LocalePicker
                localeOptions={localeOptions}
                locale={locale}
                onChangeLocale={onLocalePickerSelect}
              />
            </Container>
            <Spacer size={24} />
          </Container>
        )}
        {showTalkWithUsPrompt && (
          <TalkWithUsPrompt
            partnerID={partnerID}
            hubspotForm={hubspotForm}
            messageableID={offeringID}
          />
        )}
        <Spacer size={140} />
      </Hidden>
    </Container>
  );
}
